/**
 * PlanCard Component - Updated for new specifications
 * Implements requirements 9.2: Card format with images and links
 * - ヘッダー: プラン名/番号、所要時間、予算帯、屋内/屋外タグ（日本語のみ）
 * - タイムライン: spot → walk → spot → … 形式
 * - 最終目的地: ⭐︎アイコン＋「最終目的地」ラベル
 * - フッター: ルートを開くボタン
 */

import React, { useState, useEffect, useMemo } from 'react';
import { Plan, Spot, Walk, Location, OpenStatus } from '../types/core.js';
import SpotImageMinimal from './SpotImageMinimal.js';
import OpenStatusBadge from './OpenStatusBadge.js';
import SpotDetails from './SpotDetails.js';
import { processTagsForDisplay } from '../utils/tag-filter.js';
import { calculateStraightLineDistance, estimateWalkingTime, formatWalkingTime } from '../utils/distance-calculator.js';
import { preloadSpotImages } from '../utils/image-service.js';
import { determineOpenStatus } from '../utils/opening-hours.js';

// 営業状況を正しく判定する関数
const getSpotOpenStatus = (spot: Spot): OpenStatus => {
  // デバッグ情報をコンソールに出力
  console.log('Spot data for', spot.name, ':', {
    openStatus: spot.openStatus,
    openingHours: spot.openingHours,
    businessHours: (spot as any).businessHours,
    hours: (spot as any).hours,
    opening_hours: (spot as any).opening_hours,
    openNow: (spot as any).openNow,
    isOpen: (spot as any).isOpen,
    currentlyOpen: (spot as any).currentlyOpen,
    allFields: Object.keys(spot)
  });

  const spotData = spot as any;

  // 営業時間情報がある場合は、それを最優先で解析
  const hoursData = spot.openingHours || spotData.businessHours || spotData.hours || spotData.opening_hours;
  if (hoursData && typeof hoursData === 'string' && hoursData.trim()) {
    console.log('Found hours data, parsing:', hoursData);
    const status = determineOpenStatus({ openingHours: hoursData });
    console.log('Determined status from hours:', status);
    // 営業時間から正確に判定できた場合はそれを使用
    if (status === 'open' || status === 'closed') {
      return status;
    }
  }

  // Google Places API の営業状況フラグをチェック
  if (spotData.isOpen === true || spotData.currentlyOpen === true || spotData.openNow === true) {
    console.log('Found open flag, returning open');
    return 'open';
  }
  if (spotData.isOpen === false || spotData.currentlyOpen === false || spotData.openNow === false) {
    console.log('Found closed flag, returning closed');
    return 'closed';
  }

  // 既存のopenStatusをフォールバックとして使用（ただし、営業時間解析を優先）
  if (spot.openStatus && spot.openStatus !== 'unknown_no_data' && spot.openStatus !== 'unknown_parse_failed') {
    console.log('Using existing openStatus as fallback:', spot.openStatus);
    return spot.openStatus;
  }

  // 情報がない場合は営業中と仮定
  console.log('No reliable opening status information found, assuming open');
  return 'open';
};

export interface PlanCardProps {
  plan: Plan;
  planIndex: number;
  startLocation?: Location;
  onShare?: (plan: Plan) => void;
  className?: string;
}

export const PlanCard: React.FC<PlanCardProps> = ({
  plan,
  planIndex,
  startLocation,
  onShare,
  className = ''
}) => {
  const [showAlternatives, setShowAlternatives] = useState(false);
  const [enhancedWalkTimes, setEnhancedWalkTimes] = useState<Map<string, number>>(new Map());

  // Process tags for display (Japanese only)
  const displayTags = useMemo(() => processTagsForDisplay(plan.tags), [plan.tags]);



  // Get spots from plan and ensure final destination is marked (メモ化)
  const spots = useMemo(() => {
    const planSpots = plan.steps.filter(step => step.type === 'spot') as Spot[];

    // 最終目的地フラグを設定（最後のスポットが最終目的地）
    return planSpots.map((spot, index) => ({
      ...spot,
      isFinal: index === planSpots.length - 1
    }));
  }, [plan.steps]);

  // 実際に表示されるWalkステップのみの合計時間を計算
  const actualWalkingTime = useMemo(() => {
    const displayedWalkSteps: Walk[] = [];

    // 表示ロジックと同じフィルタリングを適用
    plan.steps.forEach((step, index) => {
      if (step.type === 'walk') {
        const nextStep = plan.steps[index + 1];
        // 最終目的地の後の移動は除外
        if (nextStep && nextStep.type === 'spot') {
          displayedWalkSteps.push(step as Walk);
        }
      }
    });

    const totalTime = displayedWalkSteps.reduce((total, walk) => total + walk.duration, 0);

    console.log('表示されるWalkステップの合計時間:', {
      allWalkStepsCount: plan.steps.filter(step => step.type === 'walk').length,
      displayedWalkStepsCount: displayedWalkSteps.length,
      displayedWalkSteps: displayedWalkSteps.map(walk => ({ duration: walk.duration, distance: walk.distance })),
      totalTime
    });

    return totalTime;
  }, [plan.steps]);

  // Google Maps APIを使用した移動時間の精度向上
  useEffect(() => {
    if (!startLocation || spots.length === 0) return;

    const calculateEnhancedTimes = async () => {
      const newTimes = new Map<string, number>();

      try {
        // Google Maps APIキーが利用可能かチェック
        const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;

        if (apiKey) {
          console.log('Google Maps APIを使用して移動時間を計算中...');

          // 現在地から最初のスポットまで
          if (spots[0]) {
            const { calculateAccurateDistance } = await import('../utils/distance-calculator');
            const result = await calculateAccurateDistance(startLocation, spots[0].location, apiKey);
            if (result.status === 'OK') {
              newTimes.set(`start-to-${spots[0].sourceId}`, result.duration);
              console.log(`Google Maps API: 現在地 → ${spots[0].name}: ${result.duration}分`);
            }
          }

          // スポット間の移動時間
          for (let i = 0; i < spots.length - 1; i++) {
            const from = spots[i];
            const to = spots[i + 1];
            const { calculateAccurateDistance } = await import('../utils/distance-calculator');
            const result = await calculateAccurateDistance(from.location, to.location, apiKey);
            if (result.status === 'OK') {
              newTimes.set(`${from.sourceId}-to-${to.sourceId}`, result.duration);
              console.log(`Google Maps API: ${from.name} → ${to.name}: ${result.duration}分`);
            }

            // API制限を考慮して少し待機
            await new Promise(resolve => setTimeout(resolve, 100));
          }
        } else {
          console.log('Google Maps APIキーが設定されていません。推定値を使用します。');

          // APIキーがない場合は推定値を使用
          if (spots[0]) {
            const distance = calculateStraightLineDistance(startLocation, spots[0].location);
            const walkTime = estimateWalkingTime(distance);
            newTimes.set(`start-to-${spots[0].sourceId}`, walkTime);
          }

          // スポット間の移動時間
          for (let i = 0; i < spots.length - 1; i++) {
            const from = spots[i];
            const to = spots[i + 1];
            const distance = calculateStraightLineDistance(from.location, to.location);
            const walkTime = estimateWalkingTime(distance);
            newTimes.set(`${from.sourceId}-to-${to.sourceId}`, walkTime);
          }
        }
      } catch (error) {
        console.error('移動時間の計算でエラーが発生しました:', error);

        // エラーの場合は推定値にフォールバック
        if (spots[0]) {
          const distance = calculateStraightLineDistance(startLocation, spots[0].location);
          const walkTime = estimateWalkingTime(distance);
          newTimes.set(`start-to-${spots[0].sourceId}`, walkTime);
        }

        for (let i = 0; i < spots.length - 1; i++) {
          const from = spots[i];
          const to = spots[i + 1];
          const distance = calculateStraightLineDistance(from.location, to.location);
          const walkTime = estimateWalkingTime(distance);
          newTimes.set(`${from.sourceId}-to-${to.sourceId}`, walkTime);
        }
      }

      setEnhancedWalkTimes(newTimes);
    };

    calculateEnhancedTimes();
  }, [startLocation?.lat, startLocation?.lng, plan.id]); // 具体的な依存関係を指定

  // スポット画像の事前読み込み（一度だけ実行）
  useEffect(() => {
    if (spots.length > 0) {
      preloadSpotImages(spots, true).catch(error => {
        console.warn('Failed to preload spot images:', error);
      });
    }
  }, [plan.id]); // plan.idが変わった時のみ実行

  const handleGoogleMapsClick = () => {
    // 現在地から全スポットを経由するルートを生成
    if (spots.length === 0) return;

    // 現在地を起点とする
    const origin = startLocation ?
      `${startLocation.lat},${startLocation.lng}` :
      `${spots[0].location.lat},${spots[0].location.lng}`;

    if (spots.length === 1) {
      // 単一スポットの場合 - スポット名を含める
      const finalSpot = spots[0];
      const destination = `${finalSpot.name}/@${finalSpot.location.lat},${finalSpot.location.lng}`;
      const url = `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${encodeURIComponent(destination)}&travelmode=walking`;
      window.open(url, '_blank', 'noopener,noreferrer');
    } else {
      // 複数スポットの場合 - 最終目的地にスポット名を含める
      const finalSpot = spots[spots.length - 1];
      const destination = `${finalSpot.name}/@${finalSpot.location.lat},${finalSpot.location.lng}`;
      const waypoints = spots.slice(0, -1).map(spot =>
        `${spot.name}/@${spot.location.lat},${spot.location.lng}`
      ).join('|');

      let url = `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${encodeURIComponent(destination)}&travelmode=walking`;
      if (waypoints) {
        url += `&waypoints=${encodeURIComponent(waypoints)}`;
      }

      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };



  const handleShareClick = () => {
    if (onShare) {
      onShare(plan);
    } else {
      // デフォルトの共有処理
      handleDefaultShare();
    }
  };

  const handleDefaultShare = async () => {
    const { generateLineShareText, generateWebShareData } = await import('../utils/share-service');

    // Web Share API対応チェック
    if (navigator.share) {
      try {
        const shareData = generateWebShareData(plan, window.location.origin);
        await navigator.share(shareData);
      } catch (error) {
        console.log('Web Share API cancelled or failed:', error);
        fallbackShare();
      }
    } else {
      fallbackShare();
    }
  };

  const fallbackShare = () => {
    // フォールバック: クリップボードにコピー
    import('../utils/share-service').then(({ generateLineShareText }) => {
      const shareText = generateLineShareText(plan, planIndex + 1);

      if (navigator.clipboard) {
        navigator.clipboard.writeText(shareText).then(() => {
          alert('共有テキストをクリップボードにコピーしました！');
        }).catch(() => {
          // クリップボードAPIが失敗した場合の処理
          showShareModal(shareText);
        });
      } else {
        showShareModal(shareText);
      }
    });
  };

  const showShareModal = (shareText: string) => {
    // 簡易的な共有モーダル表示
    const modal = document.createElement('div');
    modal.style.cssText = `
      position: fixed; top: 0; left: 0; right: 0; bottom: 0;
      background: rgba(0,0,0,0.5); z-index: 1000;
      display: flex; align-items: center; justify-content: center;
    `;

    const content = document.createElement('div');
    content.style.cssText = `
      background: white; padding: 20px; border-radius: 8px;
      max-width: 400px; margin: 20px;
    `;

    content.innerHTML = `
      <h3 style="margin-top: 0;">プランを共有</h3>
      <textarea readonly style="width: 100%; height: 150px; margin: 10px 0;">${shareText}</textarea>
      <button onclick="this.parentElement.parentElement.remove()" 
              style="background: #3b82f6; color: white; border: none; padding: 8px 16px; border-radius: 4px; cursor: pointer;">
        閉じる
      </button>
    `;

    modal.appendChild(content);
    document.body.appendChild(modal);

    // モーダル外クリックで閉じる
    modal.addEventListener('click', (e) => {
      if (e.target === modal) {
        modal.remove();
      }
    });
  };

  return (
    <div className={`bg-white rounded-lg shadow-md overflow-hidden ${className}`}>
      {/* ヘッダー */}
      <div className="p-4 border-b border-gray-200">
        <div className="mb-2">
          <h3 className="text-lg font-semibold text-gray-900">
            プラン {planIndex + 1}
          </h3>
        </div>

        <div className="flex items-center gap-4 text-sm text-gray-600 mb-2">
          <span className="flex items-center">
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {plan.estimatedDuration}分
          </span>
          <span className="flex items-center">
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 18.657A8 8 0 716.343 7.343S7 9 9 10c0-2 .5-5 2.986-7C14 5 16.09 5.777 17.656 7.343A7.975 7.975 0 0120 13a7.975 7.975 0 01-2.343 5.657z" />
            </svg>
            徒歩 {actualWalkingTime}分
          </span>
          <span className="flex items-center">
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
            </svg>
            ¥{plan.budget.min.toLocaleString()}-{plan.budget.max.toLocaleString()}
          </span>
        </div>

        {/* タグ表示（日本語のみ） */}
        {displayTags.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {displayTags.map((tag: string, index: number) => (
              <span
                key={index}
                className="inline-block px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full"
              >
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* タイムライン */}
      <div className="p-4">
        <div className="space-y-4">
          {/* スタート地点（現在地）を表示 */}
          {startLocation && (
            <StartLocationStep location={startLocation} />
          )}

          {plan.steps.map((step, index) => {
            // 最終目的地の後の移動は表示しない
            if (step.type === 'walk') {
              const nextStep = plan.steps[index + 1];
              if (!nextStep || nextStep.type !== 'spot') {
                return null; // 最終目的地の後の移動は表示しない
              }
            }

            return (
              <div key={index}>
                {step.type === 'spot' ? (
                  <SpotStep
                    spot={step as Spot}
                    stepIndex={index}
                    isLast={index === plan.steps.length - 1}
                  />
                ) : (
                  <WalkStep
                    walk={step as Walk}
                    enhancedTime={enhancedWalkTimes.get(`walk-${index}`)}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* 代替案表示 */}
      {(plan.alternatives.rain || plan.alternatives.crowd) && (
        <div className="px-4 pb-4">
          <button
            onClick={() => setShowAlternatives(!showAlternatives)}
            className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
          >
            <svg className={`w-4 h-4 mr-1 transform transition-transform ${showAlternatives ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            代替案を表示
          </button>

          {showAlternatives && (
            <div className="mt-2 space-y-2">
              {plan.alternatives.rain && (
                <div className="p-3 bg-blue-50 rounded-lg">
                  <div className="text-sm font-medium text-blue-900 mb-1">雨天時の代替案</div>
                  <div className="text-xs text-blue-700">屋内スポット中心のプラン</div>
                </div>
              )}
              {plan.alternatives.crowd && (
                <div className="p-3 bg-orange-50 rounded-lg">
                  <div className="text-sm font-medium text-orange-900 mb-1">混雑時の代替案</div>
                  <div className="text-xs text-orange-700">近接エリアの別スポット</div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* フッター */}
      <div className="p-4 bg-gray-50 border-t border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex gap-2">
            <button
              onClick={handleGoogleMapsClick}
              className="flex items-center px-3 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 transition-colors"
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              ルートを開く
            </button>


          </div>

          {onShare && (
            <button
              onClick={handleShareClick}
              className="flex items-center px-3 py-2 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 transition-colors"
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
              </svg>
              共有
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// スポットステップコンポーネント
interface SpotStepProps {
  spot: Spot;
  stepIndex: number;
  isLast: boolean;
}

const SpotStep: React.FC<SpotStepProps> = ({ spot, stepIndex, isLast }) => {
  return (
    <div className="flex gap-3">
      {/* 画像 */}
      <div className="flex-shrink-0">
        <SpotImageMinimal
          spot={spot}
          className="w-20 h-12 rounded-md"
        />
      </div>

      {/* スポット情報 */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            {/* 最終目的地の表示 */}
            {spot.isFinal && (
              <div className="flex items-center mb-1">
                <span className="text-yellow-500 mr-1">⭐️</span>
                <span className="text-xs font-medium text-yellow-700 bg-yellow-100 px-2 py-1 rounded-full">
                  最終目的地
                </span>
              </div>
            )}

            <h4 className="font-medium text-gray-900 text-left break-words">
              {spot.name}
            </h4>

            <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 mt-1">
              <span className="text-sm text-gray-600 text-left">{spot.categoryJa || spot.genre}</span>
              {/* 移動時間は表示しない（要望により削除） */}
            </div>

            <div className="flex items-center gap-2 mt-1">
              <OpenStatusBadge status={getSpotOpenStatus(spot)} />
              <span className="text-sm text-gray-600">
                滞在 {spot.duration}分
              </span>
            </div>

            {/* スポット詳細情報 */}
            <SpotDetails spot={spot} className="mt-2" />
          </div>
        </div>
      </div>
    </div>
  );
};

// 徒歩ステップコンポーネント
interface WalkStepProps {
  walk: Walk;
  enhancedTime?: number;
}

const WalkStep: React.FC<WalkStepProps> = ({ walk, enhancedTime }) => {
  const displayTime = enhancedTime || walk.duration;
  const displayDistance = Math.round(walk.distance);

  return (
    <div className="flex items-center gap-3 py-2">
      <div className="flex-shrink-0 w-20 flex justify-center">
        <div className="flex items-center justify-center w-8 h-8 bg-blue-100 rounded-full">
          <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 17l-4 4m0 0l-4-4m4 4V3" />
          </svg>
        </div>
      </div>
      <div className="flex-1">
        <div className="text-sm text-gray-600 flex items-center">
          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
          徒歩 {formatWalkingTime(displayTime)} ({displayDistance}m)
          {enhancedTime && enhancedTime !== walk.duration && (
            <span className="ml-1 text-xs text-blue-600">(精度向上)</span>
          )}
        </div>
      </div>
    </div>
  );
};

// スタート地点表示コンポーネント
interface StartLocationStepProps {
  location: Location;
}

const StartLocationStep: React.FC<StartLocationStepProps> = ({ location: _location }) => {
  return (
    <div className="flex gap-3">
      {/* スタート地点アイコン */}
      <div className="flex-shrink-0">
        <div className="w-20 h-12 bg-green-100 rounded-md flex items-center justify-center">
          <div className="text-green-600">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </div>
        </div>
      </div>

      {/* スタート地点情報 */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center mb-1">
              <span className="text-green-600 mr-1">🚩</span>
              <span className="text-xs font-medium text-green-700 bg-green-100 px-2 py-1 rounded-full">
                スタート地点
              </span>
            </div>

            <h4 className="font-medium text-gray-900 text-left break-words">
              現在地
            </h4>

            <div className="flex items-center gap-2 mt-1">
              <span className="text-sm text-gray-600 text-left">出発地点</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlanCard;